// Single KPI card. Three states (loading skeleton, ok, error). The
// optional sub-line is rendered in orange when tone === "accent" and is
// the only place we lean on the brand colour beyond focus rings — used
// for things like "3 SLA breaching" beneath an Open Orders count.
//
// When `to` is set the whole card is a Link so middle-click + ctrl-click
// behave naturally for an admin who wants to open the detail in a new
// tab. When not set the card renders as a static <div>.
import { Link } from "react-router-dom";
import { ReactNode } from "react";
import { useTranslation } from "../../i18n";

export type KpiState = "loading" | "ok" | "error";
export type KpiTone = "amber" | "green" | "blue" | "purple" | "orange" | "teal" | "slate";

interface KpiCardProps {
    labelKey: string;
    /** The headline value. null/undefined while loading or on error. */
    value: number | string | null | undefined;
    state: KpiState;
    /** Optional secondary line under the value. tone="accent" → orange. */
    subLine?: { text: string; tone?: "neutral" | "accent" } | null;
    /** Optional third line, same styling as subLine (e.g. a breakdown). */
    extraLine?: { text: string; tone?: "neutral" | "accent" } | null;
    /** When set, the card is a clickable link to this route. */
    to?: string;
    /** Shown when state === "error" so the user can re-fire the fetch. */
    onRetry?: () => void;
    /** Optional small icon shown top-right of the card. */
    icon?: ReactNode;
    /** Colour accent applied to the icon chip. Defaults to neutral grey. */
    tone?: KpiTone;
}

const KpiCard = ({ labelKey, value, state, subLine, extraLine, to, onRetry, icon, tone }: KpiCardProps) => {
    const { t } = useTranslation();

    const body = (
        <>
            <div className="admin-kpi-card-head">
                <span className="admin-kpi-card-label">{t(labelKey)}</span>
                {icon && (
                    <span className={`admin-kpi-card-icon${tone ? ` admin-kpi-card-icon--${tone}` : ""}`}>
                        {icon}
                    </span>
                )}
            </div>
            {state === "loading" && (
                <div className="admin-kpi-card-skeleton" aria-label={t("loading")} />
            )}
            {state === "error" && (
                <div className="admin-kpi-card-error">
                    <span className="admin-kpi-card-value admin-kpi-card-value--muted">—</span>
                    {onRetry && (
                        <button
                            type="button"
                            className="admin-kpi-card-retry"
                            onClick={(e) => { e.preventDefault(); onRetry(); }}
                        >
                            {t("retry")}
                        </button>
                    )}
                </div>
            )}
            {state === "ok" && (
                <div className="admin-kpi-card-value">{value ?? "—"}</div>
            )}
            {state === "ok" && subLine && (
                <div className={`admin-kpi-card-sub${subLine.tone === "accent" ? " admin-kpi-card-sub--accent" : ""}`}>
                    {subLine.text}
                </div>
            )}
            {state === "ok" && extraLine && (
                <div className={`admin-kpi-card-sub${extraLine.tone === "accent" ? " admin-kpi-card-sub--accent" : ""}`}>
                    {extraLine.text}
                </div>
            )}
        </>
    );

    const className = `admin-kpi-card${to ? " admin-kpi-card--clickable" : ""}`;

    if (to && state !== "error") {
        return (
            <Link to={to} className={className}>
                {body}
            </Link>
        );
    }
    return <div className={className}>{body}</div>;
};

export default KpiCard;
